import {
  compareCheckoffOperationFreshness,
  parseCheckoffOperationId,
} from "./grocery-checkoff-operation";

/** PostgreSQL signed bigint maximum, deliberately held as text in the browser. */
export const CHECKOFF_EPOCH_MAXIMUM = "9223372036854775807";
const CANONICAL_EPOCH = /^(?:0|[1-9][0-9]*)$/;

export interface ServerConfirmedCheckoff {
  checkoffEpoch: string;
  checkoffOperationId: string | null;
  checked: boolean;
}

export interface AuthoritativeGroceryCheckoffItem extends ServerConfirmedCheckoff {
  id: string;
}

export interface AuthoritativeGroceryCheckoffSnapshot {
  id: string;
  serverTimeMs: number;
  items: AuthoritativeGroceryCheckoffItem[];
}

export interface CheckoffEpochRecord {
  checkoffEpoch: string;
  operationId: string;
}

interface ConfirmedServerCheckoffEvidence {
  baseline: ServerConfirmedCheckoff;
  /** Present only when the authoritative response carried server time. */
  serverTimeMs?: number;
}

const confirmed = new Map<string, ConfirmedServerCheckoffEvidence>();

function targetKey(groceryListId: string, itemId: string): string {
  return JSON.stringify([groceryListId, itemId]);
}

/** Exact text comparison avoids both rounding and client-side epoch increments. */
export function compareCheckoffEpochs(left: string, right: string): number {
  if (left.length !== right.length) return left.length < right.length ? -1 : 1;
  return left === right ? 0 : left < right ? -1 : 1;
}

export function isCanonicalCheckoffEpoch(value: unknown): value is string {
  return (
    typeof value === "string" &&
    CANONICAL_EPOCH.test(value) &&
    compareCheckoffEpochs(value, CHECKOFF_EPOCH_MAXIMUM) <= 0
  );
}

export function isServerConfirmedCheckoff(
  value: unknown,
): value is ServerConfirmedCheckoff {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return (
    Object.keys(record).length === 3 &&
    isCanonicalCheckoffEpoch(record.checkoffEpoch) &&
    typeof record.checked === "boolean" &&
    (record.checkoffOperationId === null ||
      (typeof record.checkoffOperationId === "string" &&
        parseCheckoffOperationId(record.checkoffOperationId)?.version === 3))
  );
}

/**
 * Decode one complete authoritative grocery-list snapshot atomically.
 *
 * The wire list may contain arbitrary product fields, but only each item's
 * causal projection is returned. Requiring the timestamp and validating every
 * item before copying means callers can never promote a partial snapshot.
 */
export function decodeAuthoritativeGroceryCheckoffSnapshot(
  value: unknown,
): AuthoritativeGroceryCheckoffSnapshot | undefined {
  if (!value || typeof value !== "object") return undefined;
  const record = value as Record<string, unknown>;
  if (
    typeof record.id !== "string" ||
    record.id.trim().length === 0 ||
    typeof record.serverTimeMs !== "number" ||
    !Number.isSafeInteger(record.serverTimeMs) ||
    record.serverTimeMs < 0 ||
    !Array.isArray(record.items)
  ) {
    return undefined;
  }

  const items: AuthoritativeGroceryCheckoffItem[] = [];
  for (const item of record.items) {
    if (!item || typeof item !== "object") return undefined;
    const itemRecord = item as Record<string, unknown>;
    if (
      typeof itemRecord.id !== "string" ||
      itemRecord.id.trim().length === 0
    ) {
      return undefined;
    }
    const projection = {
      checkoffEpoch: itemRecord.checkoffEpoch,
      checkoffOperationId: itemRecord.checkoffOperationId,
      checked: itemRecord.checked,
    };
    if (!isServerConfirmedCheckoff(projection)) return undefined;
    items.push({ id: itemRecord.id, ...projection });
  }

  return {
    id: record.id,
    serverTimeMs: record.serverTimeMs,
    items,
  };
}

/** Only Task 7's successful authoritative GET/mutation response may call this. */
export function confirmServerCheckoff(
  groceryListId: string,
  itemId: string,
  value: ServerConfirmedCheckoff,
  serverTimeMs?: number,
): boolean {
  if (
    !isServerConfirmedCheckoff(value) ||
    (serverTimeMs !== undefined &&
      (!Number.isSafeInteger(serverTimeMs) || serverTimeMs < 0))
  ) {
    return false;
  }
  confirmed.set(targetKey(groceryListId, itemId), {
    baseline: { ...value },
    ...(serverTimeMs === undefined ? {} : { serverTimeMs }),
  });
  return true;
}

export function getConfirmedServerCheckoff(
  groceryListId: string,
  itemId: string,
): ServerConfirmedCheckoff | undefined {
  const evidence = confirmed.get(targetKey(groceryListId, itemId));
  return evidence && { ...evidence.baseline };
}

/** Server time is retained only when an authoritative response supplied it. */
export function getConfirmedServerCheckoffEvidence(
  groceryListId: string,
  itemId: string,
): ConfirmedServerCheckoffEvidence | undefined {
  const evidence = confirmed.get(targetKey(groceryListId, itemId));
  return (
    evidence && {
      baseline: { ...evidence.baseline },
      ...(evidence.serverTimeMs === undefined
        ? {}
        : { serverTimeMs: evidence.serverTimeMs }),
    }
  );
}

/** A terminal target deletion must not leave a stale local authority behind. */
export function clearConfirmedServerCheckoff(
  groceryListId: string,
  itemId: string,
): boolean {
  return confirmed.delete(targetKey(groceryListId, itemId));
}

export type CheckoffEpochAdmission =
  | { kind: "admit" }
  | { kind: "purge"; reason: "lower-epoch" | "covered" }
  | {
      kind: "quarantine";
      reason: "unconfirmed" | "higher-epoch" | "equal-epoch-above-boundary";
    }
  | { kind: "drop"; reason: "invalid" };

/**
 * Durable records are inputs, not provenance. A record cannot join an owner,
 * overlay UI, suppress work, or compact storage until this pure gate admits it.
 */
export function admitCheckoffEpochRecord(
  record: CheckoffEpochRecord,
  baseline: ServerConfirmedCheckoff | undefined,
): CheckoffEpochAdmission {
  if (
    !isCanonicalCheckoffEpoch(record.checkoffEpoch) ||
    parseCheckoffOperationId(record.operationId)?.version !== 3
  ) {
    return { kind: "drop", reason: "invalid" };
  }
  if (!baseline) return { kind: "quarantine", reason: "unconfirmed" };
  const epochOrder = compareCheckoffEpochs(
    record.checkoffEpoch,
    baseline.checkoffEpoch,
  );
  if (epochOrder < 0) return { kind: "purge", reason: "lower-epoch" };
  if (epochOrder > 0) return { kind: "quarantine", reason: "higher-epoch" };
  if (!baseline.checkoffOperationId) return { kind: "admit" };
  const candidate = parseCheckoffOperationId(record.operationId)!;
  const boundary = parseCheckoffOperationId(baseline.checkoffOperationId)!;
  return compareCheckoffOperationFreshness(candidate, boundary) <= 0
    ? { kind: "purge", reason: "covered" }
    : { kind: "quarantine", reason: "equal-epoch-above-boundary" };
}

export type LegacyRebaseResult =
  | { kind: "wait" }
  | { kind: "drop" }
  | {
      kind: "rebase";
      checkoffEpoch: string;
      operationId: string;
      checked: boolean;
    };

/** Pure and baseline-gated: callers persist this exact result at most once. */
export function rebaseLegacyCheckoff(
  legacy: { checked: boolean; rebased?: boolean },
  baseline: ServerConfirmedCheckoff | undefined,
  operationId?: string,
): LegacyRebaseResult {
  if (legacy.rebased) return { kind: "drop" };
  if (!baseline || !isServerConfirmedCheckoff(baseline) || !operationId) {
    return { kind: "wait" };
  }
  if (parseCheckoffOperationId(operationId)?.version !== 3) {
    return { kind: "drop" };
  }
  return {
    kind: "rebase",
    checkoffEpoch: baseline.checkoffEpoch,
    operationId,
    checked: legacy.checked,
  };
}

export function resetConfirmedServerCheckoffsForTests(): void {
  confirmed.clear();
}
